import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { LocalDateTime } from "@js-joda/core";
import React, { useContext } from "react";
import { AppStateContext } from "../../../context/AppStateContext";
import { fmap } from "../../../utils/fmap";
import { hasNotch } from "../../../utils/styled";

const CONNECT_GRACE_PERIOD = 2500;
const STATUS_HEIGHT = hasNotch() ? 60 : 28;

export default function ConnectionStatus() {
  const appState = useContext(AppStateContext);
  const [isConnected, setIsConnected] = React.useState<boolean>(true);
  const [isClosed, setIsClosed] = React.useState<boolean>(true);
  const [height, setHeight] = React.useState<number>(STATUS_HEIGHT);
  const [lastVisibilityChange, setLastVisibilityChange] = React.useState(LocalDateTime.now());

  React.useEffect(() => {
    const listener = () => {
      if (document.visibilityState === "visible") {
        setLastVisibilityChange(LocalDateTime.now());
      }
    };

    window.addEventListener("visibilitychange", listener);

    return () => {
      window.removeEventListener("visibilitychange", listener);
    };
  }, []);

  React.useEffect(() => {
    let markAsDisconnectedTimeout: number | undefined;
    let markAsClosedTimeout: number | undefined;

    const markAsDisconnected = () => {
      markAsDisconnectedTimeout = window.setTimeout(() => {
        console.log("appState.isRealTimeConnected", appState.isRealTimeConnected);
        if (!appState.isRealTimeConnected) {
          setIsConnected(false);
          setIsClosed(false);
        }
      }, CONNECT_GRACE_PERIOD);
    };

    markAsDisconnected();

    const onRealTimeConnected = appState.onRealTimeConnected.register(() => {
      console.log("ConnectionStatus onRealTimeConnected");
      setIsConnected(true);
      clearTimeout(markAsDisconnectedTimeout);
      markAsClosedTimeout = window.setTimeout(() => setIsClosed(true), 1500);
    });

    const onRealTimeDisconnected = appState.onRealTimeDisconnected.register(() => {
      console.log("ConnectionStatus onRealTimeDisconnected");
      markAsDisconnected();
    });

    return () => {
      onRealTimeConnected.unregister();
      onRealTimeDisconnected.unregister();
      clearTimeout(markAsDisconnectedTimeout);
      clearTimeout(markAsClosedTimeout);
    };
  }, [appState]);

  const isInGracePeriod = lastVisibilityChange.plusSeconds(5).isAfter(LocalDateTime.now());
  const shouldOpen = !isClosed && !isInGracePeriod;

  return (
    <AnimatePresence>
      {shouldOpen && (
        <Root
          ref={(e) => fmap(e?.clientHeight, setHeight)}
          transition={{ ease: "easeInOut" }}
          initial={{ y: "100%", marginTop: height * -1 }}
          animate={{ y: 0, marginTop: 0 }}
          exit={{ y: "100%", marginTop: height * -1 }}
        >
          <AnimatePresence>
            {isConnected && (
              <BackOnline
                transition={{ ease: "easeInOut" }}
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
              >
                Back online
              </BackOnline>
            )}
          </AnimatePresence>
          <NoConnection>No internet connection</NoConnection>
        </Root>
      )}
    </AnimatePresence>
  );
}

const Root = styled(motion.div)`
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
  height: ${STATUS_HEIGHT}px;
  position: relative;
  z-index: 8000;
`;

const Status = styled(motion.div)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 4px;
`;

const NoConnection = styled(Status)`
  background-color: var(--tw-slate-950);
  color: var(--tw-red-400);
  z-index: 1;
`;

const BackOnline = styled(Status)`
  background-color: var(--tw-green-600);
  color: white;
  z-index: 2;
`;
